// Single source of truth for the dark "RallyPD" design surface shared by the
// Teams page, the DarkTable component and the Create Team modal. The auth tokens
// are re-exported (not copied) so this dark palette never drifts away from the
// login/register screens that established it.
import {
  AUTH_MOBILE_ACCENT,
  AUTH_MOBILE_BG,
  AUTH_MOBILE_BUTTON_GRADIENT,
  AUTH_MOBILE_BUTTON_GRADIENT_HOVER,
  AUTH_MOBILE_INPUT_BORDER,
  AUTH_MOBILE_INPUT_BORDER_HOVER,
  DARK_SURFACE_GRADIENT
} from 'components/Layouts/AuthLayout/constants'

export {
  AUTH_MOBILE_ACCENT,
  AUTH_MOBILE_BG,
  AUTH_MOBILE_BUTTON_GRADIENT,
  AUTH_MOBILE_BUTTON_GRADIENT_HOVER,
  AUTH_MOBILE_INPUT_BORDER,
  AUTH_MOBILE_INPUT_BORDER_HOVER,
  DARK_SURFACE_GRADIENT
}

// Page background — matches the auth dark background.
export const DARK_PAGE_BG = AUTH_MOBILE_BG

// var(--Grey) from the design — used for filter/secondary button borders.
export const DARK_GREY = '#586981'

// var(--Surface-Gradient) — raised dark surfaces (table header, modal, cards).
// Defined in AuthLayout/constants and re-exported here with the other auth
// tokens, so the auth screens and this palette can never drift apart.
/** Design "LG" elevation — the drop shadow a raised surface casts on the page. */
export const DARK_SHADOW_LG = '0 10px 15px -3px rgba(0, 0, 0, 0.10), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
export const DARK_TABLE_HEADER_BG = DARK_SURFACE_GRADIENT

// Hairline border on dark surfaces (table header/rows, dividers).
export const DARK_TABLE_BORDER = 'rgba(194, 199, 214, 0.12)'

// Player assignment progress fill — Cyan/300 from the design (fixed, not the
// per-team accent which stays on the card border/badge).
export const PROGRESS_CYAN = '#76E4F7'

// Nested practice-view level fills (design #147–#150). Translucent so nesting
// levels stack into a depth ladder over the period's surface gradient:
// lane = subtle light frost, block/station = progressively darker.
export const DARK_LANE_SURFACE = 'rgba(194, 199, 214, 0.12)' // #C2C7D6 @ 12%
export const DARK_NESTED_SURFACE = 'rgba(0, 0, 0, 0.15)' // #000000 @ 15%

// Primary call-to-action gradient (same as the auth button) — var(--Button-Gradient-2).
export const CREATE_BUTTON_GRADIENT = AUTH_MOBILE_BUTTON_GRADIENT
export const CREATE_BUTTON_GRADIENT_HOVER = AUTH_MOBILE_BUTTON_GRADIENT_HOVER

/**
 * var(--Button-Stroke) — the hairline on the primary pill and on the quieter
 * toolbar pills that share its shape. Use `components/PrimaryButton` rather than
 * rebuilding that button from this token.
 */
export const DARK_BUTTON_STROKE = 'rgba(77, 179, 255, 0.30)'

/**
 * var(--Button-Stroke-Focus) — the same hue at full strength. It marks the
 * interactive edge of a control the user is meant to reach for, which is why the
 * inline edit pencils carry it rather than a muted grey.
 */
export const DARK_BUTTON_STROKE_FOCUS = '#4DB3FF'

/**
 * Shared geometry of the RallyPD pill button — 32px tall, 24px radius, the
 * `--Button-Stroke` hairline. Only the fill separates the variants: the primary
 * (`components/PrimaryButton`) carries the gradient, secondary actions such as
 * "Upload files" / "Embed links" stay transparent.
 */
export const DARK_PILL_BUTTON = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '32px',
  minHeight: '32px',
  minWidth: 'auto',
  paddingX: '12px',
  paddingY: '10px',
  gap: '8px',
  iconSpacing: '8px',
  fontSize: 'sm',
  fontWeight: 500,
  borderRadius: '24px',
  border: `1px solid ${DARK_BUTTON_STROKE}`
} as const

/**
 * Selected-state fill for tall surfaces (schedule day cells), matching the top of
 * CREATE_BUTTON_GRADIENT. A CSS gradient always spans the element's full height, so
 * that gradient's dark `#093E76` tail is invisible on a ~28px control pill but
 * lands as a separate band on a ~52px cell. Same two leading stops, no tail, so
 * both read as one colour side by side.
 */
export const SELECTED_SURFACE_GRADIENT = 'linear-gradient(180deg, #2E8FCC 0%, #1F72A6 100%)'

// var(--Navy) from the design — flat fill for "you are here" surfaces such as
// an active nav slot or an inspector section header.
export const DARK_NAVY = '#003366'

/**
 * Navy fill behind a selected navigation slot or tab. Flat, not the gradient
 * above: the gradient reads as a raised surface, this reads as "you are here".
 */
export const DARK_NAV_ACTIVE_BG = DARK_NAVY

// Selectable card (sport picker / role box) borders.
export const DARK_CARD_SELECTED_BORDER = '#4DB3FF'
export const DARK_CARD_UNSELECTED_BORDER = 'rgba(194, 199, 214, 0.30)'

/** var(--Input-Stroke-Default) — resting border of inputs on the dark surface. */
export const DARK_INPUT_BORDER = 'rgba(194, 199, 214, 0.30)'

// Text hierarchy on the dark surface.
export const DARK_TEXT_PRIMARY = '#FFFFFF'
export const DARK_TEXT_MUTED = 'rgba(255, 255, 255, 0.64)'
export const DARK_TEXT_SUBTLE = 'rgba(255, 255, 255, 0.45)'
/** Steel — muted blue-grey used for secondary labels/actions (design "Steel"). */
export const DARK_TEXT_STEEL = '#ADB2BF'
/** Light — near-white for input values and supporting caps (design "Light"). */
export const DARK_TEXT_LIGHT = '#F2F7FA'

/**
 * The quiet pill that sits beside `components/PrimaryButton` in a dark toolbar
 * ("Assignment detail", "Upload files") — the same geometry and hairline, no fill.
 */
export const DARK_SECONDARY_PILL_BUTTON = {
  ...DARK_PILL_BUTTON,
  background: 'transparent',
  color: DARK_TEXT_LIGHT,
  _hover: { background: 'rgba(255, 255, 255, 0.06)' },
  _active: { background: 'rgba(255, 255, 255, 0.1)' },
  _focus: { boxShadow: 'none' },
  _focusVisible: { boxShadow: 'none' }
} as const

// Hierarchy level name-tags on the dark practice-detail surface. Each level reads
// as a colored pill holding its own name (Lane = gold outline, Station = lavender
// fill). Block tabs reuse the active-green / neutral-inactive pair below so they
// share one look with the period tabs' inactive chip.
// Shared geometry so every level tag (Lane/Station pill + Block tab) is the exact
// same medium size with no border — only bg/color differ per level.
// 20px tall overall (16px line + 2px top/bottom), 12px semibold — the builder design.
export const DARK_TAG_BASE = {
  px: 2,
  py: '2px',
  borderRadius: '6px',
  fontSize: 'xs',
  fontWeight: 600,
  lineHeight: '16px',
  border: 'none',
  textTransform: 'none' as const
}
export const DARK_TAG_LANE = { bg: 'yellow.100', color: 'yellow.700' }
export const DARK_TAG_STATION = { bg: '#E0E2FB', color: '#5B61D6' }
// Rotation grouping accent (pink). The group is a dashed pink outline; its name is
// a pink pill (mirrors the Lane/Station tags). Selection bar uses the light-pink pair.
export const DARK_TAG_ROTATION = { bg: 'rgba(237, 100, 166, 0.16)', color: '#F687B3' }
export const DARK_ROTATION_BORDER = '#F687B3'
// "Next" reads as the quieter follow-up to the pink START HERE tag.
export const DARK_TAG_ROTATION_NEXT = { bg: 'rgba(255, 255, 255, 0.12)', color: DARK_TEXT_PRIMARY }
export const ROTATION_ACCENT = '#EC4899'
export const DARK_TAG_BLOCK_ACTIVE = { bg: '#C6F6D5', color: '#25855A' } // green.100 / green.700
// Solid fill of a collapsed hierarchy row (Day / Station). Opaque so the row reads
// the same wherever it sits on the surface gradient behind it.
export const DARK_HIERARCHY_ROW_BG = '#24303F'
export const DARK_TAG_BLOCK_INACTIVE = { bg: 'rgba(255, 255, 255, 0.08)', color: DARK_TEXT_MUTED }

// Training RX origin chips. Station RX reads in the Station tag's lavender so it
// is visibly "the station's"; a drill's Custom RX takes an orange that no level
// tag uses, so an override stands out while scanning the drill list.
export const DARK_RX_STATION = {
  bg: 'rgba(224, 226, 251, 0.10)',
  color: '#C3C6F7',
  border: 'rgba(195, 198, 247, 0.35)'
}
export const DARK_RX_CUSTOM = { bg: 'rgba(246, 173, 85, 0.14)', color: '#F6AD55', border: 'rgba(246, 173, 85, 0.45)' }

// Shared dark chrome for modals + drawers on the RallyPD practice-detail page.
// Spread DARK_MODAL_OVERLAY on <ModalOverlay/> / <DrawerOverlay/> and
// DARK_MODAL_CONTENT on <ModalContent/> / <DrawerContent/>. Inputs inside dark
// modals spread DARK_INPUT_SX via the `sx` prop (matches the inspector panel).
export const DARK_MODAL_OVERLAY = { bg: 'rgba(3, 8, 15, 0.72)' }
export const DARK_MODAL_CONTENT = {
  bg: DARK_PAGE_BG,
  color: DARK_TEXT_PRIMARY,
  border: `1px solid ${DARK_TABLE_BORDER}`,
  // The global theme forces CloseButton background to white; override it so the
  // header X reads correctly on the dark surface (every dark modal/drawer spreads
  // this object, so the fix lands everywhere at once).
  sx: {
    '.chakra-modal__close-btn': {
      background: 'transparent',
      color: DARK_TEXT_PRIMARY
    },
    '.chakra-modal__close-btn:hover': {
      background: 'rgba(255,255,255,0.08)'
    }
  }
}
/**
 * The quiet partner of `components/PrimaryButton` — the Cancel of a dark modal
 * footer. Borderless text, same 32px pill geometry so the pair lines up.
 *
 * Chakra's ghost variant sets a light `_hover` (`gray.100`) and the theme's
 * hover beats a plain `bg` style prop, so every interactive background is
 * declared here or the button flashes white.
 */
export const DARK_MODAL_CANCEL_BUTTON = {
  variant: 'ghost',
  height: '32px',
  minHeight: '32px',
  paddingX: '12px',
  fontSize: 'sm',
  fontWeight: 500,
  borderRadius: '24px',
  bg: 'transparent',
  color: DARK_TEXT_LIGHT,
  border: 'none',
  _hover: { bg: 'rgba(255, 255, 255, 0.06)' },
  _active: { bg: 'rgba(255, 255, 255, 0.1)' },
  _focus: { boxShadow: 'none' },
  _focusVisible: { boxShadow: 'none' }
} as const

/**
 * Chrome and Safari paint an autofilled field with their own pale fill, which is
 * what turned Location and Date into white boxes in the dark New Event modal.
 * The fill cannot be overridden directly — only covered by an opaque inset
 * shadow — so the dark field surface is restated here as a flat colour (the page
 * background under the field's black 15% wash) and the text colour is forced back
 * with it. The long transition keeps the fill from flashing in on focus.
 *
 * Spread inside the input's own `sx` — not into `DARK_INPUT_SX`, which several
 * callers spread as style props, where a raw selector key is not one.
 */
export const DARK_INPUT_AUTOFILL_SX = {
  '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': {
    WebkitBoxShadow: '0 0 0 1000px #0A111C inset',
    WebkitTextFillColor: DARK_TEXT_PRIMARY,
    caretColor: DARK_TEXT_PRIMARY,
    transition: 'background-color 9999s ease-in-out 0s'
  }
} as const

export const DARK_INPUT_SX = {
  bg: 'rgba(0,0,0,0.15)',
  border: `1px solid ${DARK_INPUT_BORDER}`,
  color: DARK_TEXT_PRIMARY,
  borderRadius: '8px',
  _placeholder: { color: DARK_TEXT_SUBTLE },
  _hover: { borderColor: DARK_INPUT_BORDER },
  _focus: { borderColor: '#ADB2BF', boxShadow: 'none' }
} as const

// Membership badge palette: Owned (purple), Invited (blue), Invite Pending (grey).
export const BADGE_OWNED = { color: '#C4A1FF', border: 'rgba(157, 113, 255, 0.65)', bg: 'rgba(128, 90, 213, 0.16)' }
export const BADGE_INVITED = { color: '#7FB5FF', border: 'rgba(77, 179, 255, 0.65)', bg: 'rgba(46, 143, 204, 0.16)' }
export const BADGE_PENDING = { color: '#B6BECC', border: 'rgba(194, 199, 214, 0.40)', bg: 'rgba(194, 199, 214, 0.10)' }

// Shared popup-menu chrome for every dark-surface menu (sort-by, show-rows page
// size, and the "more options" kebab menus). Spreading the same object on each
// MenuList keeps them visually identical: solid page bg + faint hairline border
// + soft drop shadow. Override minWidth/zIndex per call site after the spread.
export const DARK_MENU_LIST_PROPS = {
  bg: DARK_PAGE_BG,
  border: `1px solid ${DARK_TABLE_BORDER}`,
  boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.4)',
  borderRadius: '8px'
}

// Tint for menu trigger icons and neutral (non-destructive) menu-item icons on
// dark surfaces, so they all read the same muted white instead of their raw fill.
export const DARK_MENU_ICON_COLOR = DARK_TEXT_MUTED

// Ghost trigger chrome for kebab / dropdown buttons on dark surfaces. _active and
// _expanded stay on the dark translucent fill so the button never flashes the
// default ghost light-gray box once the menu opens and the pointer hovers back.
export const DARK_MENU_TRIGGER_SX = {
  color: DARK_TEXT_MUTED,
  bg: 'transparent',
  border: 'none',
  _hover: { bg: 'rgba(255, 255, 255, 0.08)', color: DARK_TEXT_PRIMARY },
  _active: { bg: 'rgba(255, 255, 255, 0.12)', color: DARK_TEXT_PRIMARY, border: 'none' },
  _expanded: { bg: 'rgba(255, 255, 255, 0.12)', color: DARK_TEXT_PRIMARY, border: 'none' },
  _focus: { boxShadow: 'none', border: 'none' },
  _focusVisible: { boxShadow: 'none', border: 'none' }
}

/**
 * The secondary pill every dark list toolbar uses for Filter-style actions:
 * 32px tall, muted until something is applied, then a blue tint plus a brighter
 * border. Defined once here — it was copy-pasted byte-for-byte into the Drafts,
 * Archive and Assignments toolbars, and the Stacks toolbar had drifted into its
 * own chrome.
 */
export const secondaryBtnSx = (active: boolean) => ({
  h: '32px',
  px: '16px',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: 500,
  color: active ? DARK_TEXT_PRIMARY : DARK_TEXT_MUTED,
  bg: active ? 'rgba(46,143,204,0.18)' : DARK_TABLE_HEADER_BG,
  border: `1px solid ${active ? 'rgba(77,179,255,0.55)' : DARK_GREY}`,
  _hover: { bg: 'rgba(255,255,255,0.06)' },
  _focus: { boxShadow: 'none', outline: 'none' },
  _focusVisible: { boxShadow: 'none', outline: 'none' }
})
